import { Reader } from '../bytes/reader';
import { ParseError } from '../bytes/errors';
import { concat } from '../bytes/encoding';

/** Writers for TLS-style big-endian integers and length-prefixed vectors (RFC 5246 §4). */
export const u8 = (v: number): Uint8Array => new Uint8Array([v & 0xff]);
export const u16 = (v: number): Uint8Array => new Uint8Array([(v >>> 8) & 0xff, v & 0xff]);
export const u24 = (v: number): Uint8Array => new Uint8Array([(v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff]);
export const u32 = (v: number): Uint8Array =>
  new Uint8Array([(v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff]);
export function u40(v: number): Uint8Array {
  if (!Number.isSafeInteger(v) || v < 0 || v >= 2 ** 40) throw new RangeError(`u40 out of range: ${v}`);
  return concat(u8(Math.floor(v / 0x100000000)), u32(v % 0x100000000));
}
export function u64(v: number | bigint): Uint8Array {
  const b = typeof v === 'bigint' ? v : BigInt(v);
  if (b < 0n || b > 0xffffffffffffffffn) throw new RangeError(`u64 out of range: ${v}`);
  return concat(u32(Number(b >> 32n)), u32(Number(b & 0xffffffffn)));
}

function lenPrefix(lenBytes: 1 | 2 | 3 | 4, n: number): Uint8Array {
  const max = 2 ** (8 * lenBytes) - 1;
  if (n > max) throw new RangeError(`vector length ${n} exceeds ${max}`);
  return lenBytes === 1 ? u8(n) : lenBytes === 2 ? u16(n) : lenBytes === 3 ? u24(n) : u32(n);
}

/** `opaque<…>` — length prefix followed by data. */
export function vec(lenBytes: 1 | 2 | 3 | 4, data: Uint8Array): Uint8Array {
  return concat(lenPrefix(lenBytes, data.length), data);
}

/** A vector of vectors: the outer prefix counts the total bytes of all inner items. */
export function vecOf(outerLen: 1 | 2 | 3 | 4, innerLen: 1 | 2 | 3 | 4, items: Uint8Array[]): Uint8Array {
  return vec(outerLen, concat(...items.map((i) => vec(innerLen, i))));
}

export function readVecOf(r: Reader, outerLen: 1 | 2 | 3 | 4, innerLen: 1 | 2 | 3 | 4): Uint8Array[] {
  const inner = r.subVec(outerLen);
  const items: Uint8Array[] = [];
  while (!inner.eof) items.push(inner.vec(innerLen));
  return items;
}

/** RFC 5246 §7.4.1.4.1 algorithm identifiers used by CT signatures. */
export const HashAlgorithm = { none: 0, md5: 1, sha1: 2, sha224: 3, sha256: 4, sha384: 5, sha512: 6 } as const;
export const SignatureAlgorithm = { anonymous: 0, rsa: 1, dsa: 2, ecdsa: 3 } as const;

export function hashAlgorithmName(v: number): string {
  return Object.entries(HashAlgorithm).find(([, n]) => n === v)?.[0] ?? `unknown(${v})`;
}
export function signatureAlgorithmName(v: number): string {
  return Object.entries(SignatureAlgorithm).find(([, n]) => n === v)?.[0] ?? `unknown(${v})`;
}

/** RFC 5246 §4.7 `digitally-signed` — hash alg, sig alg, u16 length, signature bytes. */
export interface DigitallySigned {
  hashAlgorithm: number;
  signatureAlgorithm: number;
  signature: Uint8Array;
}

export function readDigitallySigned(r: Reader): DigitallySigned {
  const hashAlgorithm = r.u8();
  const signatureAlgorithm = r.u8();
  const signature = r.vec(2);
  if (signature.length === 0) throw new ParseError('digitally-signed: empty signature', r.pos);
  return { hashAlgorithm, signatureAlgorithm, signature };
}

export function parseDigitallySigned(raw: Uint8Array): DigitallySigned {
  const r = new Reader(raw);
  const ds = readDigitallySigned(r);
  r.expectEnd('digitally-signed');
  return ds;
}

export function writeDigitallySigned(ds: DigitallySigned): Uint8Array {
  return concat(u8(ds.hashAlgorithm), u8(ds.signatureAlgorithm), vec(2, ds.signature));
}
